<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import MainLayout from '@/common/components/MainLayout.vue';
import { listInstalledReports } from './reports';
import { Select, Button, Card, Message } from 'primevue';
import type { ExportType, ReportServerConfig } from './models/report';
import { useReportConfigApi } from './api/getReportConfigApi';
import { useListReportApi } from './api/listReportApi';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import type { ReportResponse } from './models/ReportResponse';
import NoFilter from './reports/shared/NoFilter.vue';

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);

const selectedReportType = ref(null);
const reportSrc = ref('');

const listReportApi = useListReportApi();
const installedReports = listInstalledReports();
const enabledReports = ref<ReportResponse[]>([]);
const reports = computed(() => {
  return installedReports
    .filter((r) => enabledReports.value.some((er) => er.reportType === r.type && er.enabled))
    .map((r) => ({ name: r.name, type: r.type }));
});

const currentReport = computed(() => {
  return installedReports.find((r) => r.type === selectedReportType.value) || null;
});

const reportConfigApi = useReportConfigApi();
const reportConfig = ref<ReportServerConfig | null>(null);

onMounted(async () => {
  console.log('Mounted ReportPage.vue');
  enabledReports.value = await listReportApi.execute();
  reportConfig.value = await reportConfigApi.execute();
});

const onReportChange = () => {
  reportSrc.value = '';
};

const loading = ref(false);
const showError = ref(false);
const filterComponentRef = ref();

async function handleShowReport() {
  showError.value = false;
  exportError.value = false;

  if (!currentReport.value) return;

  loading.value = true;

  // Get filter values from the filter component
  const filterValues = filterComponentRef.value?.getFilterValues?.() || {};
  const configWithFilters = {
    reportUrl: reportConfig.value?.reportUrl || '',
    authorization: reportConfig.value?.authorization || '',
    filterValues,
  };

  reportSrc.value = await currentReport.value.showReport(configWithFilters);
  loading.value = false;
  showError.value = !reportSrc.value;
}

const exporting = ref(false);
const exportError = ref(false);
const selectedExportFormat = ref<ExportType | null>(null);
const selectedExportIcon = computed(() => {
  return selectedExportFormat.value?.icon || 'pi pi-file';
});
async function handleExportReport() {
  showError.value = false;
  exportError.value = false;

  if (!currentReport.value) return;

  if (!selectedExportFormat.value) return;

  exporting.value = true;

  // Get filter values from the filter component
  const filterValues = filterComponentRef.value?.getFilterValues?.() || {};
  const configWithFilters = {
    reportUrl: reportConfig.value?.reportUrl || '',
    authorization: reportConfig.value?.authorization || '',
    filterValues,
  };

  const result = await currentReport.value.exportReport(
    configWithFilters,
    selectedExportFormat.value.format,
  );
  exporting.value = false;

  exportError.value = !result;
}
</script>

<template>
  <MainLayout>
    <Card>
      <template #content>
        <Select
          v-model="selectedReportType"
          :options="reports"
          optionLabel="name"
          optionValue="type"
          :placeholder="translations.reportPage.selectReport"
          @change="onReportChange"
        ></Select>

        <div class="flex gap-2 justify-center place-items-center mt-2">
          <component
            class="grow"
            :is="currentReport?.filterComponent"
            ref="filterComponentRef"
            v-if="selectedReportType && currentReport?.filterComponent != NoFilter"
          ></component>

          <Button
            class="flex-none"
            :label="translations.reportPage.showReport"
            @click="handleShowReport"
            :loading="loading"
            :disabled="!selectedReportType"
            v-if="selectedReportType"
          ></Button>

          <Select
            class="flex-none"
            v-model="selectedExportFormat"
            :options="currentReport?.exportFormats"
            optionLabel="label"
            :placeholder="translations.reportPage.selectExportFormat"
            v-if="selectedReportType"
          ></Select>

          <Button
            class="flex-none"
            :label="translations.reportPage.exportReport"
            @click="handleExportReport"
            :disabled="!selectedExportFormat"
            :loading="exporting"
            :icon="selectedExportIcon"
            v-if="selectedReportType"
          ></Button>
        </div>
      </template>
    </Card>

    <Card v-if="reportSrc" class="mt-4">
      <template #content>
        <iframe id="report-viewer" v-if="reportSrc" :src="reportSrc"></iframe>
      </template>
    </Card>

    <Message v-if="showError" class="mt-4" severity="error" variant="outlined"
      >{{ translations.reportPage.showError }}
    </Message>

    <Message v-if="exportError" class="mt-4" severity="error" variant="outlined"
      >{{ translations.reportPage.exportError }}
    </Message>
  </MainLayout>
</template>

<style scoped>
#report-viewer {
  width: 100%;
  height: 80vh;
  border: none;
}
</style>
